/**
 * Icon  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { BP, Type, defineElementaryComponent } from "@adapptio/lib-blueprint";

import {
	TViewComponentDefinition,
	TComponentReact,
	Icon,
	IconProps,
	IconSourceSchema,
	ICON_SOURCE_FALLBACK_VALUE,
	propGroups,
	IIconProps,
	ICON_SIZE_CLASS_string
} from "@adapptio/lib-components";

import { termsEditor } from "../../terms";

interface ComponentIcon_State {}

const ComponentIcon_Props = {
	source: BP.Prop(
		IconSourceSchema({
			alias: "iconSource_icon",
			fallbackValue: ICON_SOURCE_FALLBACK_VALUE,
			constraints: {
				required: true
			}
		}),
		IconProps.source.order,
		propGroups.common
	),

	size: BP.Prop(IconProps.size.schema, IconProps.size.order, propGroups.common),

	foregroundColor: BP.Prop(
		IconProps.foregroundColor.schema,
		IconProps.foregroundColor.order,
		propGroups.common
	)
};

const ComponentIcon_Events = {};

const ComponentIcon_Definition = defineElementaryComponent<
	typeof ComponentIcon_Props,
	ComponentIcon_State,
	typeof ComponentIcon_Events
>({
	...termsEditor.components.icon.component,

	name: "icon",

	category: "content",

	icon: "mdi/star",

	docUrl: "...",

	order: 50,

	props: ComponentIcon_Props,

	events: ComponentIcon_Events,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: () => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentIcon_React: TComponentReact<typeof ComponentIcon_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const iconProps = { ...props };

	iconProps.size = iconProps.size as ICON_SIZE_CLASS_string;

	return (
		<Icon
			{...(iconProps as IIconProps)}
			componentPath={componentInstance.safePath}
			classList={reactComponentClassList}
			forceRender={true}
		/>
	);
};

export const ComponentIcon: TViewComponentDefinition<typeof ComponentIcon_Definition> = {
	...ComponentIcon_Definition,
	reactComponent: ComponentIcon_React
};
